import {
  BriefcaseBusiness,
  CircleAlert,
  CreditCard,
  ExternalLink,
  UserRound,
} from 'lucide-react'
import { useContext } from 'react'

import BrokersMemberContext from 'components/context/BrokersMemberContext'
import { type GrapeAPIErrorType } from 'components/hooks/GrapeAPIError'
import { adminEmployers, adminMembers, brokersMembers } from 'rails_routes'
import { Badge, Banner } from 'slab'
import { useGetBrokersMembersMemberId } from 'utils/web_api'

import EmployerReviewStatusBadge from './EmployerReviewStatusBadge'
import EmploymentStatusBadge from './EmploymentStatusBadge'
import UnitApprovalStatusBadge from './UnitApprovalStatusBadge'

const BrokersMembersSummaryInfoSkeleton = () => {
  return (
    <div className='mb-4 flex items-center gap-2'>
      {/* Employment Status Badge Skeleton */}
      <div className='inline-flex items-center rounded-lg py-1 animate-pulse'>
        <div className='h-4 w-16 bg-gray-300 rounded' />
      </div>

      {/* Employer Review Status Badge Skeleton */}
      <div className='inline-flex items-center rounded-lg py-1 animate-pulse'>
        <div className='h-4 w-20 bg-gray-300 rounded' />
      </div>

      {/* Unit Application Status Badge Skeleton */}
      <div className='inline-flex items-center rounded-lg py-1 animate-pulse'>
        <div className='h-4 w-20 bg-gray-300 rounded' />
      </div>
    </div>
  )
}

type BrokersMembersSummaryInfoProps = {
  memberId: string
  employerId: string
}

const BrokersMembersAdminNavigationBadges = ({
  memberId,
  employerId,
}: BrokersMembersSummaryInfoProps) => {
  const { isUserThatchAdmin } = useContext(BrokersMemberContext)

  if (!isUserThatchAdmin) {
    return null
  }

  return (
    <>
      <Badge
        type='info'
        leftIcon={UserRound}
        actionIcon={ExternalLink}
        onActionClick={() => {
          window.open(adminMembers.show({ id: memberId }), '_blank')
        }}
      >
        Admin View
      </Badge>
      <Badge
        type='info'
        leftIcon={BriefcaseBusiness}
        actionIcon={ExternalLink}
        onActionClick={() => {
          window.open(adminEmployers.show({ id: employerId }), '_blank')
        }}
      >
        Employer Admin View
      </Badge>
    </>
  )
}

const BrokersMembersTransactionsNavigationBadge = ({
  memberId,
}: Omit<BrokersMembersSummaryInfoProps, 'employerId'>) => {
  return (
    <Badge
      type='info'
      leftIcon={CreditCard}
      actionIcon={ExternalLink}
      onActionClick={() => {
        window.open(brokersMembers.transactions({ member_id: memberId }), '_blank')
      }}
    >
      Transactions
    </Badge>
  )
}

const BrokersMembersVipEmployerBadge = ({
  isVipEmployer,
}: {
  isVipEmployer: boolean
}) => {
  if (!isVipEmployer) {
    return null
  }

  return (
    <Badge type='slate' leftIcon={CircleAlert}>
      VIP Employer
    </Badge>
  )
}

const BrokersMembersSummaryInfo = ({
  memberId,
  employerId,
}: BrokersMembersSummaryInfoProps) => {
  const {
    data: member,
    isLoading,
    error,
  } = useGetBrokersMembersMemberId(memberId, {
    query: {
      enabled: !!memberId,
      refetchOnWindowFocus: false,
    },
  })

  if (isLoading) {
    return <BrokersMembersSummaryInfoSkeleton />
  }

  if (error) {
    return (
      <Banner title='Error loading member' type='critical'>
        {(error as GrapeAPIErrorType)?.message ||
          'An error occurred while loading the member.'}
      </Banner>
    )
  }

  if (!member) {
    return null
  }

  return (
    <div className='mb-4 flex items-center gap-2 flex-wrap flex-shrink-0'>
      {/* VIP Employer Badge */}
      <BrokersMembersVipEmployerBadge isVipEmployer={member.vip_employer || false} />

      {/* Employment Status Badge */}
      <EmploymentStatusBadge member={member} />

      {/* Employer Review Status Badge */}
      <EmployerReviewStatusBadge member={member} />

      {/* Unit Application Status Badge */}
      <UnitApprovalStatusBadge member={member} />

      {/* Transactions Navigation Badge */}
      <BrokersMembersTransactionsNavigationBadge memberId={memberId} />

      {/* Admin Navigation Badges */}
      <BrokersMembersAdminNavigationBadges
        memberId={memberId}
        employerId={employerId}
      />

      {/* COBRA Coverage Badge */}
      {member.termination_config?.post_termination_coverage === 'cobra' && (
        <Badge type='info'>🐍 Active COBRA coverage</Badge>
      )}
    </div>
  )
}

export default BrokersMembersSummaryInfo
